import gql from 'graphql-tag';
import { PageSnapshotEffectiveSimple } from '../../content-categories/gql-fragment';

export const TENANT_QUERY = gql`
  query TENANT_QUERY($id: Int!) {
    tenants_by_pk(id: $id) {
      created_at
      updated_at
      settings
      id
      name
      siteMap
      home_page_id
      home_page {
        id
        pages_snapshots_effective(order_by: { effective_at: desc }, limit: 1) {
          page_id
          name
          definition
        }
      }
      content_categories {
        id
        name
      }
      document_libraries {
        id
        name
        description
        restricted
        groups {
          group {
            id
            name
          }
        }
      }
      groups {
        id
        name
        description
      }
      settings
    }
  }
`;

export const UPDATE_TENANT_MUTATION = gql`
  mutation UPDATE_TENANT_MUTATION($id: Int!, $set: tenants_set_input) {
    update_tenants(where: { id: { _eq: $id } }, _set: $set) {
      returning {
        created_at
        updated_at
        id
        name
        home_page_id
        home_page {
          id
          pages_snapshots_effective(
            order_by: { effective_at: desc }
            limit: 1
          ) {
            page_id
            name
            definition
          }
        }
        content_categories {
          id
          name
        }
        settings
      }
    }
  }
`;

export const DELETE_DOC_LIBRARY_GROUPS_MUTATION = gql`
  mutation DELETE_DOC_LIBRARY_GROUPS_MUTATION($id: Int!) {
    delete_document_library_groups(
      where: { document_library_id: { _eq: $id } }
    ) {
      returning {
        id
      }
    }
  }
`;

export const CREATE_DOC_LIBRARY_GROUPS_MUTATION = gql`
  mutation CREATE_DOC_LIBRARY_GROUPS_MUTATION(
    $objects: [document_library_groups_insert_input!]!
  ) {
    insert_document_library_groups(objects: $objects) {
      returning {
        id
      }
    }
  }
`;

// update content categories siteMap
export const ADD_INACTIVE_PAGE_MUTATION = gql`
  mutation ADD_INACTIVE_PAGE_MUTATION(
    $pageSnapshot: pages_snapshots_insert_input!
  ) {
    insert_pages_snapshots_one(object: $pageSnapshot) {
      created_at
      updated_at
      definition
      id
      name
      chapter_responsible
      created_by
      updated_by
      page_id
      page {
        tenant {
          id
          settings
        }
        content_category_id
      }
    }
  }
`;

export const CREATE_TENANT_MUTATION = gql`
  mutation CREATE_TENANT_MUTATION($objects: [tenants_insert_input!]!) {
    insert_tenants(objects: $objects) {
      returning {
        id
        pages {
          id
          metadata
        }
      }
    }
  }
`;

export const UPDATE_TENANT_HEADINGS_MUTATION = gql`
  mutation UPDATE_TENANT_HEADINGS_MUTATION(
    $id: Int!
    $append: tenants_append_input
  ) {
    update_tenants(where: { id: { _eq: $id } }, _append: $append) {
      returning {
        id
        settings
      }
    }
  }
`;

export const DELETE_TENANT_MUTATION = gql`
  mutation DELETE_TENANT_MUTATION($id: Int!) {
    delete_master_data_configurations(where: { tenant_id: { _eq: $id } }) {
      returning {
        id
      }
    }
    delete_news(where: { tenant_id: { _eq: $id } }) {
      returning {
        id
      }
    }
    delete_pages(where: { tenant_id: { _eq: $id } }) {
      returning {
        id
      }
    }
    delete_tenants(where: { id: { _eq: $id } }) {
      returning {
        id
      }
    }
  }
`;

export const buildReferencedMasterDataQuery = (chapter, masterDataList) => {
  let finalQuery = '';
  for (const masterData of masterDataList) {
    finalQuery += `
      master_data_${masterData.id}(where: {column_${
      masterData.configuration.referenceColumnIndex
    }: {_eq: "${chapter}"}}){
        id
        ${masterData.configuration.selectedColumnIndexes
          .map((x, index) => `column_${index}`)
          .join(' ')}
      }
    `;
  }

  return gql`
  query REFERENCE_MASTER_DATA_BY_CHAPTER {
    ${finalQuery}
  }
  `;
};

export const ALL_CONTENT_CATEGORIES_QUERY = gql`
  query ALL_CONTENT_CATEGORIES_QUERY($tenantId: Int!) {
    content_categories(where: { tenant_id: { _eq: $tenantId } }) {
      name
      id
      description
      siteMap
    }
  }
  ${PageSnapshotEffectiveSimple}
`;

export const PAGES_DATA_QUERY = gql`
  query PAGES_DATA_QUERY($input: MainInput!) {
    getPagesData(input: $input) {
      pages {
        pageId
        chapter
        pageName
        contentCategoryId
        contentCategoryName
        path {
          pageId
          pageName
          level
          chapter
          title
        }
      }
    }
  }
`;

export const SUGGEST_PAGES_QUERY = gql`
  query SUGGEST_PAGES_QUERY($input: SuggestPagesInput!) {
    suggestPages(input: $input) {
      suggestions
    }
  }
`;

export const PAGES_PREVIEWS_QUERY = gql`
  query PAGES_PREVIEWS_QUERY($input: MainInput!) {
    pagesPreviews(input: $input) {
      pages
    }
  }
`;

export const UPDATE_TENANT_NAVIGATION_MUTATION = gql`
  mutation UPDATE_TENANT_NAVIGATION_MUTATION($id: Int!, $navigation: jsonb!) {
    update_tenants(
      where: { id: { _eq: $id } }
      _set: { siteMap: $navigation }
    ) {
      returning {
        id
        siteMap
      }
    }
  }
`;

export const CREATE_DOCUMENT_LIBRARY_MUTATION = gql`
  mutation CREATE_DOCUMENT_LIBRARY_MUTATION(
    $objects: [document_libraries_insert_input!]!
  ) {
    insert_document_libraries(objects: $objects) {
      returning {
        id
        name
        description
      }
    }
  }
`;

export const UPDATE_DOCUMENT_LIBRARY_MUTATION = gql`
  mutation UPDATE_DOCUMENT_LIBRARY_MUTATION(
    $id: Int!
    $set: document_libraries_set_input!
  ) {
    update_document_libraries(where: { id: { _eq: $id } }, _set: $set) {
      returning {
        id
        name
        description
      }
    }
  }
`;

export const DELETE_DOCUMENT_LIBRARY_MUTATION = gql`
  mutation DELETE_DOCUMENT_LIBRARY_MUTATION($id: Int!) {
    delete_document_libraries(where: { id: { _eq: $id } }) {
      returning {
        id
      }
    }
  }
`;

export const PAGES_WITH_RESPONSIBLES_QUERY = gql`
  query PAGES_WITH_RESPONSIBLES_QUERY($tenantId: Int!) {
    pages(where: { tenant_id: { _eq: $tenantId } }) {
      id
      pages_snapshots_effective(order_by: { effective_at: desc }, limit: 1) {
        id
        page_id
        name
        is_active
        chapter_responsible
      }
    }
  }
`;

export const CONTENT_CATEGORY_STRUCTURE = gql`
  query CONTENT_CATEGORY_STRUCTURE($input: ContentCategoryStructureInput!) {
    getContentCategoryStructure(input: $input) {
      structure
      pagesDetailsOfSiteMap
    }
  }
`;
